import React, { useState, useEffect } from 'react';
import { useNavigation } from '../context/NavigationContext';
import { useAuth } from '../../context/AuthContext';
import { getTurkishAuthErrorMessage } from '../../services/firebaseAuthService';
import {
  Sparkles,
  Lock,
  Mail,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  KeyRound,
  X,
  Send,
  UserCheck,
} from 'lucide-react';

export const LoginPage: React.FC = () => {
  const { navigate } = useNavigation();
  const {
    currentUser,
    isAuthenticated,
    isSuperAdmin,
    loginWithEmail,
    loginWithGoogle,
    sendPasswordReset,
  } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Password reset modal state
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // If already authenticated, redirect to appropriate portal
  useEffect(() => {
    if (isAuthenticated && currentUser && !success) {
      const timer = setTimeout(() => {
        const isSuper = Boolean(
          currentUser.email?.toLowerCase() === 'admin@adiosoft.com' &&
          (currentUser.uid === 'zrL9FtQF42aSt7cNDDVrzBAYOEP2' || isSuperAdmin || currentUser.role === 'super_admin' || currentUser.role === 'SUPER_ADMIN')
        );
        if (isSuper) {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, currentUser, isSuperAdmin, navigate, success]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const userData = await loginWithEmail(email, password);
      setSuccess(true);
      const isSuper = Boolean(
        userData.email?.toLowerCase() === 'admin@adiosoft.com' &&
        (userData.uid === 'zrL9FtQF42aSt7cNDDVrzBAYOEP2' || userData.role === 'super_admin' || userData.role === 'SUPER_ADMIN' || isSuperAdmin)
      );
      setTimeout(() => {
        if (isSuper) {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }, 400);
    } catch (err: any) {
      setError(getTurkishAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const userData = await loginWithGoogle();
      setSuccess(true);
      const isSuper = Boolean(
        userData.email?.toLowerCase() === 'admin@adiosoft.com' &&
        (userData.role === 'super_admin' || userData.role === 'SUPER_ADMIN' || isSuperAdmin)
      );
      setTimeout(() => {
        if (isSuper) {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }, 500);
    } catch (err: any) {
      setError(getTurkishAuthErrorMessage(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    setResetLoading(true);
    try {
      await sendPasswordReset(resetEmail);
      setResetSuccess(true);
    } catch (err: any) {
      setResetError(getTurkishAuthErrorMessage(err));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-center py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-600/15 blur-[120px] pointer-events-none" />

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        {/* Brand Header */}
        <div
          id="login-brand-logo-btn"
          onClick={() => navigate('/')}
          className="flex items-center justify-center gap-3 cursor-pointer group mb-6"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-sky-400 flex items-center justify-center shadow-md shadow-blue-600/30 group-hover:scale-105 transition-transform">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-2xl font-bold tracking-tight text-white font-sans">
              Adio<span className="text-blue-400">soft</span>
            </span>
            <span className="text-[10px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
              Builder
            </span>
          </div>
        </div>

        <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
          Yönetici Paneline Giriş Yap
        </h2>
        <p className="mt-2 text-center text-xs sm:text-sm text-slate-400">
          Henüz hesabınız yok mu?{' '}
          <button
            id="login-go-to-register-btn"
            onClick={() => navigate('/kayit')}
            className="font-semibold text-blue-400 hover:text-blue-300 transition underline underline-offset-4"
          >
            14 Gün Ücretsiz Başlayın
          </button>
        </p>
      </div>

      {/* Main Login Card */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-slate-900/90 border border-slate-800 py-8 px-6 sm:px-8 rounded-3xl shadow-2xl">
          {isAuthenticated && currentUser ? (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center justify-center mx-auto">
                <UserCheck className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold text-white">Giriş Yapılmış Durumda</h3>
              <p className="text-xs text-slate-400">
                <span className="text-slate-200 font-semibold">{currentUser.displayName || currentUser.email}</span> olarak oturum açık.
              </p>
              <div className="pt-2">
                <button
                  id="already-logged-go-dash-btn"
                  onClick={() => {
                    if (isSuperAdmin || currentUser?.role === 'super_admin' || currentUser?.role === 'SUPER_ADMIN') {
                      navigate('/admin');
                    } else {
                      navigate('/dashboard');
                    }
                  }}
                  className="w-full py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 transition"
                >
                  <span>
                    {isSuperAdmin || currentUser?.role === 'super_admin' || currentUser?.role === 'SUPER_ADMIN'
                      ? 'Şirket Yönetim Paneline Git (/admin)'
                      : 'Müşteri Paneline Git (/dashboard)'}
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : success ? (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold text-white">Giriş Başarılı!</h3>
              <p className="text-xs text-slate-400">
                Yönetici kontrol paneline aktarılıyorsunuz...
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Google One-Click Login */}
              <button
                id="google-login-btn"
                type="button"
                disabled={googleLoading || loading}
                onClick={handleGoogleLogin}
                className="w-full py-3 px-4 rounded-xl bg-slate-950 hover:bg-slate-800 border border-slate-700 text-slate-200 font-semibold text-xs sm:text-sm flex items-center justify-center gap-3 transition shadow-sm disabled:opacity-50"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                  />
                </svg>
                <span>{googleLoading ? 'Google Bağlantısı...' : 'Google ile Giriş Yap'}</span>
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-[1px] bg-slate-800" />
                <span className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">veya E-Posta ile</span>
                <div className="flex-1 h-[1px] bg-slate-800" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-xs flex items-start gap-2.5">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{error}</span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    E-Posta Adresi
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      id="login-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ornek@sirketiniz.com"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-medium text-slate-300">
                      Şifre
                    </label>
                    <button
                      id="forgot-password-btn"
                      type="button"
                      onClick={() => {
                        setResetEmail(email);
                        setResetSuccess(false);
                        setResetError(null);
                        setIsResetModalOpen(true);
                      }}
                      className="text-[11px] text-blue-400 hover:text-blue-300 transition"
                    >
                      Şifremi Unuttum?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      id="login-password-input"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••••"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-400">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="rounded bg-slate-950 border-slate-800 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Beni hatırla (30 gün)</span>
                  </label>
                </div>

                <button
                  id="login-submit-btn"
                  type="submit"
                  disabled={loading || googleLoading}
                  className="w-full py-3.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 transition active:scale-95 disabled:opacity-50"
                >
                  {loading ? (
                    <span>Doğrulanıyor...</span>
                  ) : (
                    <>
                      <span>Giriş Yap</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Security callout */}
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-4 h-4 text-slate-600" />
          <span>256-Bit SSL Şifreli Güvenli Giriş</span>
        </div>
      </div>

      {/* Password Reset Modal */}
      {isResetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl relative">
            <button
              onClick={() => setIsResetModalOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white p-1 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-600/20 text-blue-400 border border-blue-500/30 flex items-center justify-center">
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Şifre Sıfırlama</h3>
                <p className="text-xs text-slate-400">Güvenli e-posta sıfırlama bağlantısı</p>
              </div>
            </div>

            {resetSuccess ? (
              <div className="text-center py-4 space-y-3">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-bold text-white">Sıfırlama Bağlantısı Gönderildi!</h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  <strong className="text-white">{resetEmail}</strong> adresine şifre sıfırlama bağlantısı gönderildi. Lütfen gelen kutunuzu ve spam klasörünüzü kontrol edin.
                </p>
                <div className="pt-2">
                  <button
                    onClick={() => setIsResetModalOpen(false)}
                    className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold"
                  >
                    Tamam
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSendResetEmail} className="space-y-4">
                <p className="text-xs text-slate-400 leading-relaxed">
                  Hesabınıza kayıtlı e-posta adresinizi girin. Size yeni şifre oluşturabileceğiniz güvenli bir bağlantı göndereceğiz.
                </p>

                {resetError && (
                  <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-xs flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{resetError}</span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    E-Posta Adresiniz
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      required
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="ornek@sirketiniz.com"
                      className="w-full pl-9 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsResetModalOpen(false)}
                    className="px-4 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 text-xs font-medium hover:bg-slate-800 transition"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold flex items-center gap-2 shadow-lg shadow-blue-600/30 transition disabled:opacity-50"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span>{resetLoading ? 'Gönderiliyor...' : 'Bağlantıyı Gönder'}</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
